/**
 * Gerador de comandos ESC/POS para impressão de cupons.
 * Extraído do CaixaProdutosMemory para reutilização no Totem e demais módulos.
 */
import { isPlatform } from '@ionic/react';
import { adicionarImpressaoNaFila, criarJobImpressao } from './PrintQueueHelper';
import { urlBaseCore } from './Services';
import { sanitizarTextoParaEscPos } from './EscPosText';

function removerAcentosECedilha(texto: string): string {
    return texto
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ç/g, 'c')
        .replace(/Ç/g, 'C');
}

function aplicarMascaraCPFouCNPJ(texto: any) {
    if (!texto) return '';
    texto = texto.replace(/\D/g, '');
    if (texto.length === 11) {
        return texto.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (texto.length === 14) {
        return texto.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return texto;
}

function formataTelefone(numero: any) {
    if (numero?.length === 13) {
        const m = numero.replace('55', '').match(/(\d{2})(\d{5})(\d{4})/);
        return m ? `(${m[1]}) ${m[2]}-${m[3]}` : '';
    } else if (numero?.length === 12) {
        const m = numero.replace('55', '').match(/(\d{2})(\d{4})(\d{4})/);
        return m ? `(${m[1]}) ${m[2]}-${m[3]}` : '';
    }
    return '';
}

function quebrarTextoEmLinhas(texto: any, tamanhoMaximo: any) {
    const conteudo = String(texto || '').trim();
    if (!conteudo) return [];

    const palavras = conteudo.split(/\s+/);
    const linhas: string[] = [];
    let linhaAtual = '';

    palavras.forEach((palavra: any) => {
        let restante = String(palavra);

        while (restante.length > 0) {
            const proximaLinha = linhaAtual ? `${linhaAtual} ${restante}` : restante;

            if (proximaLinha.length <= tamanhoMaximo) {
                linhaAtual = proximaLinha;
                restante = '';
            } else if (linhaAtual) {
                linhas.push(linhaAtual);
                linhaAtual = '';
            } else {
                linhas.push(restante.slice(0, tamanhoMaximo));
                restante = restante.slice(tamanhoMaximo);
            }
        }
    });

    if (linhaAtual) linhas.push(linhaAtual);
    return linhas;
}

const formatarValor = (valor: any) => {
    if (!valor) return '0,00';
    const valorNumerico = parseFloat(valor.toString().replace(/\./g, '').replace(',', '.'));
    return valorNumerico.toFixed(2).replace('.', ',');
};

/**
 * Gera comandos ESC/POS a partir dos dados do pedido (projeto) e da empresa.
 * Mesma lógica usada no CaixaProdutosMemory.
 */
export function gerarEscPos(
    projeto: any,
    dadosEmpresa: any,
    imprimir58mm: boolean = false,
    fonteGrande: boolean = false
): Uint8Array | null {

    function gerarCabecalho(proj: any, empresa: any) {
        const ESC = 0x1B;
        const SUBLINHADO_ON = [ESC, 0x2D, 0x01];
        const SUBLINHADO_OFF = [ESC, 0x2D, 0x00];

        let cabecalhoText = '';
        cabecalhoText += `${empresa?.empresaNome}\n`;
        cabecalhoText += `CNPJ: ${aplicarMascaraCPFouCNPJ(empresa?.empresaCnpj)}\n`;
        quebrarTextoEmLinhas(`Endereço: ${empresa.empresaEndereco}`, imprimir58mm ? 32 : 38).forEach((linha: any) => {
            cabecalhoText += `${linha}\n`;
        });
        cabecalhoText += `Tel: ${formataTelefone(empresa?.empresaTelefone)}\n`;

        if (proj.dataCupom) {
            cabecalhoText += `Data: ${new Date(proj.dataCupom).toLocaleDateString('pt-BR')} Hora: ${new Date(proj.dataCupom).toLocaleTimeString('pt-BR')}\n`;
        }

        cabecalhoText += imprimir58mm ? `********************************\n` : `**************************************\n`;

        cabecalhoText += `${proj.clienteNome}\n`;

        if (!proj.interno) {
            cabecalhoText += `Tel: ${formataTelefone(proj.clienteTelefone)}\n`;
            quebrarTextoEmLinhas(`Endereço: ${[proj.endereco, proj.complemento].filter(Boolean).join(', ') || ''}`, imprimir58mm ? 32 : 38).forEach((linha: any) => {
                cabecalhoText += `${linha}\n`;
            });
        }

        cabecalhoText += imprimir58mm ? `********************************\n` : `**************************************\n`;
        cabecalhoText += 'CUPOM NÃO FISCAL\n';

        if (proj.nomeProjeto) {
            cabecalhoText += imprimir58mm ? `================================\n` : `======================================\n`;
            quebrarTextoEmLinhas(`${empresa?.servicoTipo || 'Projeto'}: ${proj.nomeProjeto}`, imprimir58mm ? 32 : 38).forEach((linha: string) => {
                cabecalhoText += `${String.fromCharCode(...SUBLINHADO_ON)}${linha}${String.fromCharCode(...SUBLINHADO_OFF)}\n`;
            });
            cabecalhoText += imprimir58mm ? `================================\n` : `======================================\n`;
        }

        return cabecalhoText;
    }

    function gerarProdutos(proj: any) {
        const ESC = 0x1B;
        const SUBLINHADO_ON = [ESC, 0x2D, 0x01];
        const SUBLINHADO_OFF = [ESC, 0x2D, 0x00];

        let produtosText = '';
        const larguraProduto = imprimir58mm ? 32 : 38;

        const adicionarLinhasProduto = (
            texto: string,
            prefixoPrimeiraLinha: string,
            prefixoDemaisLinhas: string = ' '.repeat(prefixoPrimeiraLinha.length),
            sublinhar: boolean = false
        ) => {
            const larguraConteudo = Math.max(8, larguraProduto - Math.max(prefixoPrimeiraLinha.length, prefixoDemaisLinhas.length));

            quebrarTextoEmLinhas(texto, larguraConteudo).forEach((linha: string, indiceLinha: number) => {
                const prefixo = indiceLinha === 0 ? prefixoPrimeiraLinha : prefixoDemaisLinhas;

                if (sublinhar) {
                    produtosText += `${prefixo}${String.fromCharCode(...SUBLINHADO_ON)}${linha}${String.fromCharCode(...SUBLINHADO_OFF)}\n`;
                } else {
                    produtosText += `${prefixo}${linha}\n`;
                }
            });
        };

        produtosText += 'QTDE | DESCRIÇÃO\n';
        produtosText += imprimir58mm ? `--------------------------------\n` : `--------------------------------------\n`;

        if (proj.produtos && Array.isArray(proj.produtos) && proj.produtos.length > 0) {
            proj.produtos.forEach((produto: any) => {
                if (Array.isArray(produto.descricao)) {
                    produto.descricao.forEach((itemDesc: any, indexLinha: number) => {
                        if (indexLinha === 0) {
                            const prefixoPrincipal = `${produto.quantidade} | `;
                            const descricaoPrincipal = produto.tipo === 'pizzas'
                                ? `${produto.tamanho} ${produto.quantidadeSabores} ${produto.quantidadeSabores === 1 ? "SABOR" : "SABORES"} (R$ ${formatarValor(produto.valor)})`
                                : `${produto.categoria} (R$ ${formatarValor(produto.valor)})`;

                            adicionarLinhasProduto(descricaoPrincipal, prefixoPrincipal);
                        }
                        if (itemDesc.descricao) {
                            if (itemDesc.categoria === 'Observação') {
                                adicionarLinhasProduto(`Observação: ${itemDesc.descricao}`, '  ', '  ', true);
                            } else {
                                adicionarLinhasProduto(`${itemDesc.descricao} (R$ ${formatarValor(itemDesc.valor)})`, '  - ', '    ');
                            }
                        }
                    });
                } else {
                    adicionarLinhasProduto(`${produto.descricao} (R$ ${formatarValor(produto.valor)})`, `${produto.quantidade} | `);
                }
                produtosText += `VALOR: R$ ${formatarValor(produto.subtotal)}\n`;
                produtosText += imprimir58mm ? `--------------------------------\n` : `--------------------------------------\n`;
            });

            if (proj.valorTotalProdutos) {
                produtosText += `TOTAL: R$ ${formatarValor(proj.valorTotalProdutos)}\n`;
            }
        }

        return produtosText;
    }

    function gerarFooter(proj: any, empresa: any) {
        let pagamentoText = '';
        const larguraRodape = imprimir58mm ? 32 : 38;

        const adicionarLinhasRodape = (texto: string) => {
            quebrarTextoEmLinhas(texto, larguraRodape).forEach((linha: string) => {
                pagamentoText += `${linha}\n`;
            });
        };

        const linhasFormaPagamento = Array.isArray(proj.formaPagamento)
            ? proj.formaPagamento
                .map((fp: any) => {
                    const nome = sanitizarTextoParaEscPos(fp?.pagamento || fp?.formaPagamentoNome || fp?.tipo);
                    const valor = fp?.valor ?? fp?.formaPagamentoValor;

                    if (!nome) {
                        return '';
                    }

                    return `${String(nome).toUpperCase()}: R$ ${formatarValor(valor)}`;
                })
                .filter(Boolean)
            : [];

        if (proj.extraInfo) {
            adicionarLinhasRodape(`INFORMAÇÕES EXTRAS: ${proj.extraInfo}`);
        }

        if (proj.formaPagamento) {
            if (!Array.isArray(proj.formaPagamento)) {
                pagamentoText += imprimir58mm ? `********************************\n` : `**************************************\n`;
                const linhaPagamento = proj.formaPagamento?.toLowerCase() === 'dinheiro' && proj.valorPagoDinheiro
                    ? `DINHEIRO: R$ ${formatarValor(proj.valorPagoDinheiro)}`
                    : `FORMA PAGAMENTO: ${sanitizarTextoParaEscPos(proj.formaPagamento).toUpperCase()}`;

                adicionarLinhasRodape(linhaPagamento);
            }
            else if (linhasFormaPagamento.length > 0) {
                pagamentoText += imprimir58mm ? `********************************\n` : `**************************************\n`;
                pagamentoText += `FORMAS DE PAGAMENTO:\n`;
                linhasFormaPagamento.forEach((linha: string) => adicionarLinhasRodape(linha));
            }
        }

        if (proj.troco && proj.troco !== '0,00') {
            pagamentoText += `TROCO: R$ ${formatarValor(proj.troco)}\n`;
        }

        if (proj.taxaEntrega) {
            pagamentoText += `TAXA DE ENTREGA: +R$ ${formatarValor(proj.taxaEntrega)}\n`;
        }

        if (proj.desconto) {
            pagamentoText += `DESCONTO: -R$ ${formatarValor(proj.desconto)}\n`;
        }

        if (proj.valorTotal) {
            pagamentoText += imprimir58mm ? `********************************\n` : `**************************************\n`;
            pagamentoText += `TOTAL ${empresa.servicoTipo?.toUpperCase() || 'PROJETO'}: R$ ${formatarValor(proj.valorTotal)}\n`;
        }

        return pagamentoText;
    }

    try {
        const cabecalho = gerarCabecalho(projeto, dadosEmpresa);
        const produtos = gerarProdutos(projeto);
        const rodape = gerarFooter(projeto, dadosEmpresa);

        const ESC = 0x1B;
        const LF = 0x0A;
        const GS = 0x1D;
        const CORTA_PAPEL = [ESC, 0x69];
        const NEGRITO_ON = [ESC, 0x45, 0x01];
        const NEGRITO_OFF = [ESC, 0x45, 0x00];
        const FONTE_NORMAL = [GS, 0x21, 0x00];
        const FONTE_GRANDE = [GS, 0x21, 0x01];

        const dadosImpressao: any = [
            LF,
            ...(fonteGrande ? FONTE_GRANDE : FONTE_NORMAL),
            ...NEGRITO_ON,
            ...removerAcentosECedilha(cabecalho).split('').map(c => c.charCodeAt(0)),
            ...NEGRITO_OFF,
            LF,
            ...removerAcentosECedilha(produtos).split('').map(c => c.charCodeAt(0)),
            LF,
            ...removerAcentosECedilha(rodape).split('').map(c => c.charCodeAt(0)),
            ...FONTE_NORMAL,
            LF, LF, LF, LF, LF,
            ...CORTA_PAPEL,
        ];

        return new Uint8Array(dadosImpressao);
    } catch (error) {
        console.error('Erro ao gerar ESC/POS:', error);
        return null;
    }
}

/**
 * Imprime um cupom do pedido usando a mesma rotina do CaixaFechar.
 * Detecta o tipo de impressora configurada e envia para a fila de impressão.
 */
export function imprimirCupom(projeto: any, dadosEmpresa: any, origem: string = 'Totem:imprimirCupom'): boolean {
    const comandosEscPos = gerarEscPos(
        projeto,
        dadosEmpresa,
        dadosEmpresa.impressoraLargura === '58',
        dadosEmpresa.tamanhoFonte === 'grande'
    );

    if (!comandosEscPos) return false;

    const quantidadeImpressoes = dadosEmpresa.impressaoDuasVias ? 2 : 1;
    const mobile = !isPlatform('mobileweb') && isPlatform('mobile');

    const impressoraRedeHabilitar = window.localStorage.getItem('impressoraRedeHabilitar') === 'true';
    const impressoraLocalHabilitar = window.localStorage.getItem('impressoraLocalHabilitar') === 'true';
    const impressoraBluetoothHabilitar = window.localStorage.getItem('impressoraBluetoothHabilitar') === 'true';
    const impressoraBluetoothSPPHabilitar = window.localStorage.getItem('impressoraBluetoothSPPHabilitar') === 'true';

    let tipo: 'ble' | 'spp' | 'rede' | 'local' | null = null;
    let config: any = {};

    if (impressoraRedeHabilitar && mobile) {
        tipo = 'rede';
        config = { ip: dadosEmpresa.impressoraRedeIp, porta: dadosEmpresa.impressoraRedePorta };
    } else if (impressoraLocalHabilitar && !mobile) {
        tipo = 'local';
        config = { printerName: window.localStorage.getItem('impressoraLocalName') || '' };
    } else if (impressoraBluetoothHabilitar && mobile) {
        tipo = 'ble';
        config = { deviceId: window.localStorage.getItem('impressoraBluetoothDeviceId') };
    } else if (impressoraBluetoothSPPHabilitar && isPlatform('android') && mobile) {
        tipo = 'spp';
        config = { address: window.localStorage.getItem('impressoraBluetoothSPPAddress') };
    }

    if (tipo) {
        return adicionarImpressaoNaFila(criarJobImpressao({
            comandosEscPos,
            dados: projeto,
            impressoraConfig: config,
            tipo,
            quantidadeVias: quantidadeImpressoes,
            origem
        }));
    }

    return false;
}

async function carregarProjetoCupom(projetoid: any) {
    const token = localStorage.getItem("token") || "";
    const resp = await fetch(urlBaseCore.url + "/ordens/" + String(projetoid).replaceAll(/#/g, "%23") + "/semassinar", {
        method: urlBaseCore.method,
        headers: [["token", token]],
        credentials: 'include'
    });
    const response = await resp.json();
    return response?.Item;
}

export async function imprimirCupomComFallbackData(params: {
    projetoid?: any;
    projeto?: any;
    dadosEmpresa: any;
    origem: string;
}): Promise<boolean> {
    const projetoImpressao = params.projeto || await carregarProjetoCupom(params.projetoid);
    if (!projetoImpressao) return false;

    const adicionado = imprimirCupom(projetoImpressao, params.dadosEmpresa, params.origem);
    if (adicionado) {
        console.log(`📋 [CUPOM] Cupom adicionado na fila: ${params.origem}`);
    }

    return adicionado;
}

export function gerarEscPosFechamentoCaixa(
    fechamento: any,
    dadosEmpresa: any,
    imprimir58mm: boolean = false,
    fonteGrande: boolean = false
): Uint8Array | null {
    const formatarValorFechamento = (valor: any) => {
        const valorNumerico = Number(valor || 0);
        return valorNumerico.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatarDataFechamento = (data: any) => {
        if (!data) return '';
        return new Date(data).toLocaleString('pt-BR').replace(',', '');
    };

    const adicionarLinhaValor = (texto: string, valor: any) => `${texto}: R$ ${formatarValorFechamento(valor)}\n`;
    const linhaSeparadora = imprimir58mm ? `********************************\n` : `**************************************\n`;
    const largura = imprimir58mm ? 32 : 38;

    try {
        const caixa = fechamento?.caixa || {};
        const lancamentos = Array.isArray(fechamento?.lancamentos) ? fechamento.lancamentos : [];
        const pagamentos = fechamento?.pagamentos || {};
        const tiposVenda = fechamento?.tiposVenda || {};
        const totais = fechamento?.totais || {};

        let texto = '';
        texto += `${dadosEmpresa?.empresaNome || ''}\n`;
        texto += `CNPJ: ${aplicarMascaraCPFouCNPJ(dadosEmpresa?.empresaCnpj)}\n`;
        quebrarTextoEmLinhas(`Endereço: ${dadosEmpresa?.empresaEndereco || ''}`, largura).forEach((linha: string) => {
            texto += `${linha}\n`;
        });
        texto += `Tel: ${formataTelefone(dadosEmpresa?.empresaTelefone)}\n`;
        texto += linhaSeparadora;
        texto += `FECHAMENTO DE CAIXA\n`;
        texto += linhaSeparadora;
        texto += `Status: ${caixa.status || ''}\n`;
        texto += `Abertura: ${formatarDataFechamento(caixa.dataAbertura)}\n`;
        if (caixa.dataFechamento) {
            texto += `Fechamento: ${formatarDataFechamento(caixa.dataFechamento)}\n`;
        }
        texto += `Emissao: ${formatarDataFechamento(new Date().toISOString())}\n`;
        texto += linhaSeparadora;
        texto += adicionarLinhaValor('Vendas', totais.vendas);
        texto += adicionarLinhaValor('Abertura', totais.abertura);
        texto += adicionarLinhaValor('Lancamentos', totais.lancamentos);
        texto += adicionarLinhaValor('Dinheiro', totais.dinheiro);
        texto += linhaSeparadora;
        texto += `TOTAL POR TIPO DE VENDA\n`;
        Object.entries(tiposVenda).forEach(([nome, valor]: any) => {
            if (Number(valor) > 0) {
                texto += adicionarLinhaValor(nome, valor);
            }
        });
        texto += linhaSeparadora;
        texto += `FORMAS DE PAGAMENTO\n`;
        Object.entries(pagamentos).forEach(([nome, valor]: any) => {
            if (Number(valor) > 0) {
                texto += adicionarLinhaValor(nome, valor);
            }
        });
        texto += linhaSeparadora;
        texto += `LANCAMENTOS\n`;
        texto += adicionarLinhaValor('Entradas', totais.entradas);
        texto += adicionarLinhaValor('Saidas', totais.saidas);
        texto += adicionarLinhaValor('Saldo', totais.lancamentos);
        texto += linhaSeparadora;

        if (lancamentos.length === 0) {
            texto += `Nenhum lancamento registrado\n`;
        } else {
            lancamentos.forEach((lancamento: any) => {
                texto += `${formatarDataFechamento(lancamento.data)}\n`;
                quebrarTextoEmLinhas(lancamento.descricao || '', largura).forEach((linha: string) => {
                    texto += `${linha}\n`;
                });
                texto += `${Number(lancamento.valor || 0) >= 0 ? '+' : '-'} R$ ${formatarValorFechamento(Math.abs(Number(lancamento.valor || 0)))}\n`;
                texto += imprimir58mm ? `--------------------------------\n` : `--------------------------------------\n`;
            });
        }

        const ESC = 0x1B;
        const LF = 0x0A;
        const GS = 0x1D;
        const CORTA_PAPEL = [ESC, 0x69];
        const NEGRITO_ON = [ESC, 0x45, 0x01];
        const NEGRITO_OFF = [ESC, 0x45, 0x00];
        const FONTE_NORMAL = [GS, 0x21, 0x00];
        const FONTE_GRANDE = [GS, 0x21, 0x01];

        const dadosImpressao: any = [
            LF,
            ...(fonteGrande ? FONTE_GRANDE : FONTE_NORMAL),
            ...NEGRITO_ON,
            ...removerAcentosECedilha(texto).split('').map(c => c.charCodeAt(0)),
            ...NEGRITO_OFF,
            ...FONTE_NORMAL,
            LF, LF, LF, LF, LF,
            ...CORTA_PAPEL,
        ];

        return new Uint8Array(dadosImpressao);
    } catch (error) {
        console.error('Erro ao gerar ESC/POS do fechamento de caixa:', error);
        return null;
    }
}

/**
 * Gera comandos ESC/POS para cupom parcial de cozinha.
 * Cabeçalho reduzido (só nome da empresa + data + cliente), sem preços totais, sem CNPJ/endereço.
 * Mesma lógica usada no ComandasProdutosMemory.gerarEscPosParcial.
 */
export function gerarEscPosParcial(
    projeto: any,
    dadosEmpresa: any,
    imprimir58mm: boolean = false,
    fonteGrande: boolean = false
): Uint8Array | null {

    function gerarCabecalho(proj: any, empresa: any) {
        let cabecalhoText = '';
        cabecalhoText += `${empresa?.empresaNome}\n`;

        if (proj.dataCupom) {
            cabecalhoText += `Data: ${new Date(proj.dataCupom).toLocaleDateString('pt-BR')} Hora: ${new Date(proj.dataCupom).toLocaleTimeString('pt-BR')}\n`;
        }

        cabecalhoText += imprimir58mm ? `********************************\n` : `**************************************\n`;
        cabecalhoText += `${proj.clienteNome}\n`;

        if (proj.nomeProjeto) {
            quebrarTextoEmLinhas(`Pedido: ${proj.nomeProjeto}`, imprimir58mm ? 32 : 38).forEach((linha: string) => {
                cabecalhoText += `${linha}\n`;
            });
        }

        return cabecalhoText;
    }

    function gerarProdutos(proj: any) {
        const ESC = 0x1B;
        const SUBLINHADO_ON = [ESC, 0x2D, 0x01];
        const SUBLINHADO_OFF = [ESC, 0x2D, 0x00];

        let produtosText = '';
        const larguraProduto = imprimir58mm ? 32 : 38;

        const adicionarLinhasProduto = (
            texto: string,
            prefixoPrimeiraLinha: string,
            prefixoDemaisLinhas: string = ' '.repeat(prefixoPrimeiraLinha.length),
            sublinhar: boolean = false
        ) => {
            const larguraConteudo = Math.max(8, larguraProduto - Math.max(prefixoPrimeiraLinha.length, prefixoDemaisLinhas.length));

            quebrarTextoEmLinhas(texto, larguraConteudo).forEach((linha: string, indiceLinha: number) => {
                const prefixo = indiceLinha === 0 ? prefixoPrimeiraLinha : prefixoDemaisLinhas;

                if (sublinhar) {
                    produtosText += `${prefixo}${String.fromCharCode(...SUBLINHADO_ON)}${linha}${String.fromCharCode(...SUBLINHADO_OFF)}\n`;
                } else {
                    produtosText += `${prefixo}${linha}\n`;
                }
            });
        };

        produtosText += 'QTDE | DESCRIÇÃO\n';
        produtosText += imprimir58mm ? `--------------------------------\n` : `--------------------------------------\n`;

        if (proj.produtos && Array.isArray(proj.produtos) && proj.produtos.length > 0) {
            proj.produtos.filter((prod: any) => !prod.impresso).forEach((produto: any) => {
                if (Array.isArray(produto.descricao)) {
                    produto.descricao.forEach((itemDesc: any, indexLinha: number) => {
                        if (indexLinha === 0) {
                            const prefixoPrincipal = `${produto.quantidade} | `;
                            const descricaoPrincipal = produto.tipo === 'pizzas'
                                ? `${produto.tamanho} ${produto.quantidadeSabores} ${produto.quantidadeSabores === 1 ? "SABOR" : "SABORES"} (R$ ${formatarValor(produto.valor)})`
                                : `${produto.categoria} (R$ ${formatarValor(produto.valor)})`;

                            adicionarLinhasProduto(descricaoPrincipal, prefixoPrincipal);
                        }
                        if (itemDesc.descricao) {
                            if (itemDesc.categoria === 'Observação') {
                                adicionarLinhasProduto(`Observação: ${itemDesc.descricao}`, '  ', '  ', true);
                            } else {
                                adicionarLinhasProduto(`${itemDesc.descricao} (R$ ${formatarValor(itemDesc.valor)})`, '  - ', '    ');
                            }
                        }
                    });
                } else {
                    adicionarLinhasProduto(`${produto.descricao} (R$ ${formatarValor(produto.valor)})`, `${produto.quantidade} | `);
                }
                produtosText += `VALOR: R$ ${formatarValor(produto.subtotal)}\n`;
                produtosText += imprimir58mm ? `--------------------------------\n` : `--------------------------------------\n`;
            });
        }

        return produtosText;
    }

    try {
        const cabecalho = gerarCabecalho(projeto, dadosEmpresa);
        const produtos = gerarProdutos(projeto);

        const ESC = 0x1B;
        const LF = 0x0A;
        const GS = 0x1D;
        const CORTA_PAPEL = [ESC, 0x69];
        const NEGRITO_ON = [ESC, 0x45, 0x01];
        const NEGRITO_OFF = [ESC, 0x45, 0x00];
        const FONTE_NORMAL = [GS, 0x21, 0x00];
        const FONTE_GRANDE = [GS, 0x21, 0x01];

        const dadosImpressao: any = [
            LF,
            ...(fonteGrande ? FONTE_GRANDE : FONTE_NORMAL),
            ...NEGRITO_ON,
            ...removerAcentosECedilha(cabecalho).split('').map(c => c.charCodeAt(0)),
            ...NEGRITO_OFF,
            LF,
            ...removerAcentosECedilha(produtos).split('').map(c => c.charCodeAt(0)),
            ...FONTE_NORMAL,
            LF, LF, LF, LF, LF,
            ...CORTA_PAPEL,
        ];

        return new Uint8Array(dadosImpressao);
    } catch (error) {
        console.error('Erro ao gerar ESC/POS parcial:', error);
        return null;
    }
}

/**
 * Imprime cupom de cozinha (parcial) nas impressoras adicionais de produção.
 *
 * Só age quando há multi-impressão configurada (multiImpressao > 1).
 * Com uma única impressora, o cupom completo de imprimirCupom já cobre o cenário —
 * a impressora pode estar na cozinha e não faz sentido imprimir um ticket parcial adicional.
 */
export function imprimirCupomCozinha(projeto: any, dadosEmpresa: any): boolean {

    const imprimir58mm = dadosEmpresa.impressoraLargura === '58';
    const fonteGrande = dadosEmpresa.tamanhoFonte === 'grande';
    const mobile = !isPlatform('mobileweb') && isPlatform('mobile');

    function resolverImpressora(impressoraNome?: string): { tipo: 'ble' | 'spp' | 'rede' | 'local'; config: any } | null {
        // Busca impressora adicional pelo nome
        if (impressoraNome) {
            const impressoraConfig = dadosEmpresa?.impressorasAdicionais?.find(
                (imp: any) => imp.nome === impressoraNome
            );

            if (impressoraConfig?.conexoes && Array.isArray(impressoraConfig.conexoes)) {
                for (const conexao of impressoraConfig.conexoes) {
                    if (mobile) {
                        if (conexao.tipo === 'rede' && conexao.redeIp && conexao.redePorta) {
                            return { tipo: 'rede', config: { ip: conexao.redeIp, porta: parseInt(conexao.redePorta || '9100', 10) } };
                        }
                        if (conexao.tipo === 'ble' && conexao.bluetoothDeviceId) {
                            return { tipo: 'ble', config: { deviceId: conexao.bluetoothDeviceId } };
                        }
                        if (conexao.tipo === 'spp' && isPlatform('android') && conexao.bluetoothSPPAddress) {
                            return { tipo: 'spp', config: { address: conexao.bluetoothSPPAddress } };
                        }
                    } else {
                        if (conexao.tipo === 'local' && conexao.localName) {
                            return { tipo: 'local', config: { printerName: conexao.localName } };
                        }
                    }
                }
            }
        }

        const impressoraRedeHabilitar = window.localStorage.getItem('impressoraRedeHabilitar') === 'true';
        const impressoraLocalHabilitar = window.localStorage.getItem('impressoraLocalHabilitar') === 'true';
        const impressoraBluetoothHabilitar = window.localStorage.getItem('impressoraBluetoothHabilitar') === 'true';
        const impressoraBluetoothSPPHabilitar = window.localStorage.getItem('impressoraBluetoothSPPHabilitar') === 'true';

        // Fallback: impressora principal
        if (impressoraRedeHabilitar && mobile) {
            return { tipo: 'rede', config: { ip: dadosEmpresa.impressoraRedeIp, porta: dadosEmpresa.impressoraRedePorta } };
        }
        if (impressoraLocalHabilitar && !mobile) {
            return { tipo: 'local', config: { printerName: window.localStorage.getItem('impressoraLocalName') || '' } };
        }
        if (impressoraBluetoothHabilitar && mobile) {
            return { tipo: 'ble', config: { deviceId: window.localStorage.getItem('impressoraBluetoothDeviceId') } };
        }
        if (impressoraBluetoothSPPHabilitar && isPlatform('android') && mobile) {
            return { tipo: 'spp', config: { address: window.localStorage.getItem('impressoraBluetoothSPPAddress') } };
        }

        return null;
    }

    // Multi-impressão: agrupa produtos por impressoraSelecionada
    if (dadosEmpresa?.multiImpressao && dadosEmpresa.multiImpressao > 1) {
        const produtosPorImpressora: Record<string, any[]> = {};

        projeto.produtos?.filter((prod: any) => !prod.impresso).forEach((produto: any) => {
            const impressora = produto.impressoraSelecionada || 'principal';
            if (!produtosPorImpressora[impressora]) {
                produtosPorImpressora[impressora] = [];
            }
            produtosPorImpressora[impressora].push(produto);
        });

        let algumSucesso = false;
        for (const impressora in produtosPorImpressora) {
            const projetoParaImpressora = { ...projeto, produtos: produtosPorImpressora[impressora] };
            const comandos = gerarEscPosParcial(projetoParaImpressora, dadosEmpresa, imprimir58mm, fonteGrande);
            if (!comandos) continue;

            const dest = resolverImpressora(impressora === 'principal' ? undefined : impressora);
            if (dest) {
                const ok = adicionarImpressaoNaFila(criarJobImpressao({
                    comandosEscPos: comandos,
                    dados: projetoParaImpressora,
                    impressoraConfig: dest.config,
                    tipo: dest.tipo,
                    quantidadeVias: 1,
                    origem: `Cozinha:multi(${impressora})`
                }));
                if (ok) algumSucesso = true;
            }
        }
        return algumSucesso;
    }

    return false;
}
